import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import * as api from '../utils/api';

const Subscription = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState(null);
  const [wallets, setWallets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTier, setSelectedTier] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    loadSubscriptionInfo();
  }, [isAuthenticated, navigate]);

  const loadSubscriptionInfo = async () => {
    try {
      const response = await api.getSubscriptionTiers();
      setTiers(response.tiers);
      setWallets(response.wallets);
    } catch (error) {
      console.error('Error loading subscription info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = (tier, duration) => {
    setSelectedTier({ tier, duration });
    setShowPaymentModal(true);
  };

  if (loading) {
    return <div className="spinner"></div>;
  }

  return (
    <div className="container fade-in" data-testid="subscription-page">
      <div className="mystical-bg"></div>
      <h1 className="text-center mb-4 glow-text" style={{fontSize: '3rem'}}>
        🔮 Choose Your Path
      </h1>
      
      {user && (
        <div className="text-center mb-4">
          <p style={{color: '#8b5cf6', fontSize: '1.2rem'}}>
            Current Tier: <strong>{tiers?.[user.subscription_tier]?.name || 'Free'}</strong>
          </p>
          {user.subscription_tier === 'free' && (
            <p style={{color: '#cbd5e1'}}>
              Weekly Readings: {user.weekly_readings_used}/3
            </p>
          )}
          {user.subscription_tier !== 'free' && (
            <p style={{color: '#cbd5e1'}}>
              Daily Readings: {user.readings_today}/{user.daily_reading_limit}
            </p>
          )}
          <Link to="/crypto-guide" style={{color: '#8b5cf6', textDecoration: 'underline', marginTop: '0.5rem', display: 'inline-block'}}>
            🎓 New to Crypto? Learn how to pay →
          </Link>
        </div>
      )}

      <div className="grid grid-3" style={{marginTop: '3rem'}}>
        {/* Free Tier */}
        <div className="card" style={{border: user?.subscription_tier === 'free' ? '3px solid #8b5cf6' : ''}}>
          <h2 style={{fontSize: '2rem', marginBottom: '1rem', color: '#cbd5e1'}}>Free Tier</h2>
          <div style={{fontSize: '3rem', fontWeight: 'bold', color: '#8b5cf6', marginBottom: '1rem'}}>
            $0
          </div>
          <ul style={{listStyle: 'none', padding: 0, marginBottom: '2rem'}}>
            {tiers?.free?.features.map((feature, idx) => (
              <li key={idx} style={{marginBottom: '0.75rem', color: '#cbd5e1'}}>
                ✓ {feature}
              </li>
            ))}
          </ul>
          {user?.subscription_tier === 'free' && (
            <button className="btn btn-secondary" style={{width: '100%'}} disabled>
              Current Tier
            </button>
          )}
        </div>

        {/* Basic Tier */}
        <div className="card" style={{border: user?.subscription_tier === 'basic' ? '3px solid #8b5cf6' : '', transform: 'scale(1.05)'}}>
          <div style={{background: 'linear-gradient(135deg, #8b5cf6, #ec4899)', padding: '0.5rem', borderRadius: '0.5rem', marginBottom: '1rem'}}>
            <p style={{color: 'white', fontWeight: 'bold', textAlign: 'center', margin: 0}}>POPULAR</p>
          </div>
          <h2 style={{fontSize: '2rem', marginBottom: '1rem', color: '#cbd5e1'}}>Basic Tier</h2>
          <div style={{marginBottom: '1rem'}}>
            <div style={{fontSize: '3rem', fontWeight: 'bold', color: '#8b5cf6'}}>$6<span style={{fontSize: '1rem'}}>/mo</span></div>
            <p style={{color: '#9ca3af', fontSize: '0.9rem'}}>$30 for 6 months • $50 for 1 year</p>
          </div>
          <ul style={{listStyle: 'none', padding: 0, marginBottom: '2rem'}}>
            {tiers?.basic?.features.map((feature, idx) => (
              <li key={idx} style={{marginBottom: '0.75rem', color: '#cbd5e1'}}>
                ✓ {feature}
              </li>
            ))}
          </ul>
          {user?.subscription_tier === 'basic' ? (
            <button className="btn btn-secondary" style={{width: '100%'}} disabled>
              Current Tier
            </button>
          ) : (
            <div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
              <button onClick={() => handleUpgrade('basic', 'monthly')} className="btn btn-primary" style={{width: '100%'}}>
                $6/month
              </button>
              <button onClick={() => handleUpgrade('basic', '6months')} className="btn btn-secondary" style={{width: '100%'}}>
                $30/6 months
              </button>
              <button onClick={() => handleUpgrade('basic', 'annual')} className="btn btn-secondary" style={{width: '100%'}}>
                $50/year
              </button>
            </div>
          )}
        </div>

        {/* Top Tier */}
        <div className="card" style={{border: user?.subscription_tier === 'top' ? '3px solid #f59e0b' : ''}}>
          <div style={{background: 'linear-gradient(135deg, #f59e0b, #ef4444)', padding: '0.5rem', borderRadius: '0.5rem', marginBottom: '1rem'}}>
            <p style={{color: 'white', fontWeight: 'bold', textAlign: 'center', margin: 0}}>PREMIUM</p>
          </div>
          <h2 style={{fontSize: '2rem', marginBottom: '1rem', color: '#cbd5e1'}}>Top Tier</h2>
          <div style={{marginBottom: '1rem'}}>
            <div style={{fontSize: '3rem', fontWeight: 'bold', color: '#f59e0b'}}>$12<span style={{fontSize: '1rem'}}>/mo</span></div>
            <p style={{color: '#9ca3af', fontSize: '0.9rem'}}>$60 for 6 months • $100 for 1 year</p>
          </div>
          <ul style={{listStyle: 'none', padding: 0, marginBottom: '2rem'}}>
            {tiers?.top?.features.map((feature, idx) => (
              <li key={idx} style={{marginBottom: '0.75rem', color: '#cbd5e1'}}>
                ✓ {feature}
              </li>
            ))}
          </ul>
          {user?.subscription_tier === 'top' ? (
            <button className="btn btn-secondary" style={{width: '100%'}} disabled>
              Current Tier
            </button>
          ) : (
            <div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
              <button onClick={() => handleUpgrade('top', 'monthly')} className="btn btn-primary" style={{width: '100%', background: 'linear-gradient(135deg, #f59e0b, #ef4444)'}}>
                $12/month
              </button>
              <button onClick={() => handleUpgrade('top', '6months')} className="btn btn-secondary" style={{width: '100%'}}>
                $60/6 months
              </button>
              <button onClick={() => handleUpgrade('top', 'annual')} className="btn btn-secondary" style={{width: '100%'}}>
                $100/year
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedTier && (
        <PaymentModal
          tier={selectedTier}
          wallets={wallets}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedTier(null);
          }}
        />
      )}
    </div>
  );
};

// Payment Modal Component
const PaymentModal = ({ tier, wallets, onClose }) => {
  const [network, setNetwork] = useState('solana');
  const [txHash, setTxHash] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const prices = {
    basic: { monthly: 6, '6months': 30, annual: 50 },
    top: { monthly: 12, '6months': 60, annual: 100 }
  };

  const amount = prices[tier.tier][tier.duration];
  const selectedWallet = wallets[network];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await api.submitPayment({
        subscription_tier: `${tier.tier}_${tier.duration}`,
        transaction_hash: txHash,
        wallet_address: walletAddress,
        amount_usdt: amount,
        network: network,
        notes: notes
      });
      setSuccess(true);
    } catch (error) {
      alert('Error submitting payment: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <h2 className="text-center glow-text" style={{fontSize: '2rem', marginBottom: '1rem'}}>✅ Payment Submitted!</h2>
          <p style={{textAlign: 'center', color: '#cbd5e1', marginBottom: '2rem'}}>
            Your payment has been submitted for verification. An admin will activate your subscription soon!
          </p>
          <button onClick={onClose} className="btn btn-primary" style={{width: '100%'}}>
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '700px'}}>
        <h2 className="text-center glow-text" style={{fontSize: '2rem', marginBottom: '1rem'}}>
          💳 Pay {amount} USDT
        </h2>

        <div style={{marginBottom: '2rem', padding: '1rem', background: 'rgba(139, 92, 246, 0.1)', borderRadius: '0.5rem'}}>
          <p style={{color: '#8b5cf6', fontWeight: 'bold'}}>Selected: {tier.tier.toUpperCase()} - {tier.duration}</p>
          <p style={{color: '#cbd5e1'}}>Amount: {amount} USDT</p>
        </div>

        <div className="form-group">
          <label className="form-label">Select Payment Method</label>
          <select className="form-select" value={network} onChange={(e) => setNetwork(e.target.value)}>
            <option value="solana">Crypto - Solana (USDC/USDT)</option>
            <option value="evm">Crypto - EVM Networks (USDT)</option>
            <option value="wise">Cash/USD - WISE App</option>
          </select>
        </div>

        {/* Crypto Payment Section */}
        {(network === 'solana' || network === 'evm') && (
          <div style={{padding: '1rem', background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.5rem', marginBottom: '1rem'}}>
            <p style={{fontWeight: 'bold', color: '#8b5cf6', marginBottom: '0.5rem'}}>
              {selectedWallet.network}
            </p>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
              <p style={{fontSize: '0.85rem', wordBreak: 'break-all', color: '#cbd5e1', fontFamily: 'monospace', flex: 1, margin: 0}}>
                {selectedWallet.address}
              </p>
              <button
                type="button"
                onClick={() => copyToClipboard(selectedWallet.address)}
                style={{
                  background: copied ? 'linear-gradient(135deg, #22c55e, #16a34a)' : 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                  border: 'none',
                  borderRadius: '0.5rem',
                  padding: '0.5rem 1rem',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.3s ease'
                }}
              >
                {copied ? '✅ Copied!' : '📋 Copy'}
              </button>
            </div>
            {network === 'evm' && (
              <p style={{fontSize: '0.8rem', color: '#9ca3af', marginTop: '0.5rem'}}>
                Supported: {selectedWallet.networks_supported.join(', ')}
              </p>
            )}
          </div>
        )}

        {/* WISE Payment Section */}
        {network === 'wise' && (
          <div style={{padding: '1.5rem', background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.5rem', marginBottom: '1rem'}}>
            <p style={{fontWeight: 'bold', color: '#22c55e', marginBottom: '1rem', fontSize: '1.1rem'}}>
              💵 Pay ${amount} USD via WISE App
            </p>
            
            {/* QR Code */}
            <div style={{textAlign: 'center', marginBottom: '1rem'}}>
              <img 
                src="https://customer-assets.emergentagent.com/job_f762d5c9-eb11-408d-adfe-a6974644b398/artifacts/wq86fhkv_Screenshot%202026-01-06%20at%2012.22.10%E2%80%AFAM.png"
                alt="WISE Payment QR Code"
                style={{maxWidth: '200px', borderRadius: '0.5rem', border: '2px solid rgba(34, 197, 94, 0.3)'}}
              />
            </div>
            
            {/* WISE Link */}
            <div style={{textAlign: 'center', marginBottom: '1rem'}}>
              <a 
                href="https://wise.com/pay/me/kelvint17" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn btn-primary"
                style={{display: 'inline-block'}}
              >
                🔗 Pay via WISE Link
              </a>
            </div>
            
            {/* Important Note */}
            <div style={{padding: '1rem', background: 'rgba(245, 158, 11, 0.1)', borderRadius: '0.5rem', border: '1px solid rgba(245, 158, 11, 0.3)'}}>
              <p style={{color: '#f59e0b', fontWeight: 'bold', marginBottom: '0.5rem'}}>⚠️ Important:</p>
              <p style={{color: '#cbd5e1', fontSize: '0.9rem', marginBottom: '0'}}>
                Take a <strong>screenshot</strong> of your payment receipt with the <strong>Transaction ID visible</strong>. 
                Keep this as proof of payment in case any issues arise.
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">
              {network === 'wise' ? 'Your Email or WISE Username' : 'Your Wallet Address (that sent payment)'}
            </label>
            <input
              type="text"
              className="form-input"
              value={walletAddress}
              onChange={(e) => setWalletAddress(e.target.value)}
              placeholder={network === 'wise' ? 'Your email or WISE username' : 'Your wallet address'}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              {network === 'wise' ? 'WISE Transaction ID' : 'Transaction Hash'} (Optional)
            </label>
            <input
              type="text"
              className="form-input"
              value={txHash}
              onChange={(e) => setTxHash(e.target.value)}
              placeholder={network === 'wise' ? 'WISE Transaction ID from receipt' : 'Transaction hash'}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Notes (Optional)</label>
            <textarea
              className="form-textarea"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional notes"
              rows={2}
            />
          </div>

          {/* Contact Info */}
          <div style={{padding: '1rem', background: 'rgba(139, 92, 246, 0.1)', borderRadius: '0.5rem', marginBottom: '1rem'}}>
            <p style={{color: '#9ca3af', fontSize: '0.85rem', marginBottom: '0'}}>
              📧 Questions or issues? Contact us at: <a href="mailto:NinjasOfSatoshi@proton.me" style={{color: '#8b5cf6'}}>NinjasOfSatoshi@proton.me</a>
            </p>
          </div>

          <div style={{display: 'flex', gap: '1rem'}}>
            <button type="button" onClick={onClose} className="btn btn-secondary" style={{flex: 1}}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" style={{flex: 1}} disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit Payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Subscription;
